import {
  ButtonIcon,
  IconX,
  IconPlusAltActive,
} from '@spotify-internal/encore-web';
import { useT } from 'client/i18n/hooks/useT';
import { FieldInput } from 'components/encore/form/Field';
import { css } from 'emotion';
import { PollFormData } from 'modules/AnchorAPI/v3/episodes/fetchInteractivityPoll';
import React from 'react';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormRegister,
} from 'react-hook-form';
import {
  OptionsContainer,
  OptionsInputsGridContainer,
  StyledFieldLabel,
} from '../InteractivePoll/styles';

export function OptionsFieldArray({
  control,
  register,
  errors,
}: {
  control: Control<PollFormData>;
  register: UseFormRegister<PollFormData>;
  errors?: FieldErrors;
}) {
  const { fields, append, remove } = useFieldArray<PollFormData>({
    name: 'options',
    control,
  });
  const t = useT();

  return (
    <OptionsContainer>
      <StyledFieldLabel htmlFor="options">
        {t(
          'EpisodeEditorPublish.InteractivePollForm.OptionsFieldArray.ChoicesFieldLabel',
          'Poll choices',
          'Poll choices field label'
        )}
      </StyledFieldLabel>
      <p
        className={css`
          margin-bottom: 1.6rem;
        `}
      >
        {t(
          'EpisodeEditorPublish.InteractivePollForm.OptionsFieldArray.ChoicesFieldInstructions',
          "You'll need at least 2 choices and a maximum of 7 to publish a poll.",
          'Poll choices field instructions'
        )}
      </p>
      <OptionsInputsGridContainer>
        <div>
          {fields.map((option, i) => {
            const error = errors?.options && errors.options[i]?.description;
            return (
              <div
                key={`${option.id}`}
                className={css`
                  margin-bottom: 1.6rem;
                  width: 100%;
                  display: flex;
                  align-items: center;
                `}
              >
                <FieldInput
                  error={error}
                  containerClassName={css`
                    flex: 1;
                  `}
                  type="text"
                  placeholder={t(
                    'EpisodeEditorPublish.InteractivePollForm.OptionsFieldArray.OptionFieldPlaceholder',
                    'option #{number}',
                    'Option number field placeholder',
                    { number: i + 1 }
                  )}
                  {...register(`options.${i}.description`, {
                    required: t(
                      'EpisodeEditorPublish.InteractivePollForm.OptionsFieldArray.OptionFieldRequired',
                      'Required',
                      'Option required text'
                    ),
                  })}
                />
                <ButtonIcon
                  onClick={() => remove(i)}
                  disabled={fields.length <= 2}
                  className={css`
                    margin-left: 8px;
                  `}
                >
                  <IconX
                    aria-label={
                      option.description
                        ? t(
                            'EpisodeEditorPublish.InteractivePollForm.OptionsFieldArray.RemoveOptionButtonAriaLabel',
                            'Remove "{description}" option',
                            'Remove option button screenreader information',
                            { description: option.description }
                          )
                        : t(
                            'EpisodeEditorPublish.InteractivePollForm.OptionsFieldArray.RemoveOptionRowButtonAriaLabel',
                            'Remove option row',
                            'Remove option row button screenreader information'
                          )
                    }
                  />
                </ButtonIcon>
              </div>
            );
          })}
        </div>
      </OptionsInputsGridContainer>
      {fields.length < 7 && (
        <ButtonIcon
          onClick={() => append({ description: '' })}
          semanticColor="textBrightAccent"
          className={css`
            display: flex;
            align-items: center;
          `}
        >
          <IconPlusAltActive
            className={css`
              margin-right: 8px;
            `}
          />
          {t('Button.AddOption', 'Add option', 'Add option button text')}
        </ButtonIcon>
      )}
    </OptionsContainer>
  );
}
